import { LogOut, Menu, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { LanguageSwitch } from '@/components/language-switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NotificationBell } from '@/features/notifications';
import { useAuth } from '@/lib/auth/auth-context';
import { activeNavTo, NAV_ITEMS } from './nav-config';

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '?';

const EXTRA_TITLES: Array<{ prefix: string; key: string }> = [
  { prefix: '/awards/templates', key: 'awards.templates.title' },
  { prefix: '/my/applications', key: 'nav.myApplications' },
  { prefix: '/my/profile', key: 'nav.myProfile' },
];

function titleKeyFor(pathname: string): string | null {
  // A page outside the nav with its own title (templates, the applicant's own pages) first; otherwise the
  // section the sidebar highlights, so the two can never disagree.
  const extra = EXTRA_TITLES.find((item) => pathname === item.prefix || pathname.startsWith(`${item.prefix}/`));
  if (extra) return extra.key;
  const to = activeNavTo(pathname);
  return NAV_ITEMS.find((item) => item.to === to)?.labelKey ?? null;
}

/**
 * Top bar — drawer toggle, page title, tenant chip, notification bell,
 * language, user menu.
 *
 * Opaque white (not a frosted wash) so it reads as chrome, matching the
 * awards-frontend shell. The title is the current section so you always know
 * where you are without scanning the rail.
 *
 * Its height is `--header-h`, shared with the rail's logo block: the two bottom
 * rules meet as one line across the top of the app, which is only true while
 * both read the same variable.
 */
export function Topbar({ onMenuToggle }: { onMenuToggle?: () => void }) {
  const { t } = useTranslation();
  const { user, logout, signOutSending } = useAuth();
  const { pathname } = useLocation();
  const titleKey = titleKeyFor(pathname);

  return (
    <header className="sticky top-0 z-20 flex h-[var(--header-h)] items-center gap-3 border-b bg-card px-4 sm:px-6">
      <button
        type="button"
        onClick={onMenuToggle}
        aria-label={/* Open navigation */ t('nav.openMenu')}
        className="grid size-9 place-items-center rounded-xl border text-foreground transition-colors hover:bg-muted lg:hidden"
      >
        <Menu className="size-[18px]" aria-hidden />
      </button>

      {titleKey ? (
        <p className="truncate text-[15px] font-semibold tracking-tight text-secondary-foreground">{t(titleKey)}</p>
      ) : null}

      <div className="ml-auto flex items-center gap-2.5">
        {user && user.kind !== 'APPLICANT' ? (
          // Names WHICH organisation this session is acting in. Someone who
          // staffs two tenants was previously told only that they were in one
          // of them, which is the fact least worth stating. Falls back to the
          // generic label when the name cannot be read, never to a blank chip.
          <span
            className="hidden max-w-[14rem] truncate rounded-md border bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground sm:inline"
            title={user.tenantName ?? undefined}
          >
            {user.tenantId ? (user.tenantName ?? /* Organisation */ t('dashboard.tenant')) : /* Platform */ t('common.platform')}
          </span>
        ) : null}
        {user && user.kind !== 'APPLICANT' && user.tenantId ? (
          // Only staff acting in a tenant have a bell; for anyone else it is
          // not rendered, so it never fetches or streams (spec AC1.1).
          <NotificationBell />
        ) : null}
        <LanguageSwitch />
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 rounded-xl outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            <span className="grid size-9 place-items-center rounded-xl bg-primary text-[12px] font-semibold text-primary-foreground">
              {initials(user?.displayName ?? '')}
            </span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="truncate">{user?.displayName}</span>
                <span className="truncate text-xs font-normal text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <User /> {user ? t(`nav.kind.${user.kind}`, { defaultValue: user.kind }) : ''}
            </DropdownMenuItem>
            {/* NI-F: while sign-out sends the answers, the item says so and a second click does nothing. */}
            <DropdownMenuItem disabled={signOutSending} onSelect={() => void logout()}>
              <LogOut />{' '}
              {signOutSending ? /* Sending your answers… */ t('auth.signOutUnsaved.sending') : /* Sign out */ t('auth.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
